export type AutosaveStatus = 'Idle' | 'Saving' | 'Saved' | 'Error' | 'Conflict';

export type AutosaveSaveInput<Value> = {
  value: Value;
  expectedRevision: number;
};

export type AutosaveSaveResult<Value> = {
  value: Value;
  revision: number;
};

export type AutosaveConflict<Value> = {
  serverValue: Value;
  serverRevision: number;
};

export type AutosaveSnapshot<Value> = {
  status: AutosaveStatus;
  value: Value;
  revision: number;
  dirty: boolean;
  error: unknown | null;
  conflict: AutosaveConflict<Value> | null;
};

type BeforeUnloadLike = {
  preventDefault(): void;
  returnValue?: string;
};

type AutosaveOptions<Value> = {
  initialValue: Value;
  initialRevision: number;
  debounceMs: number;
  mode?: 'automatic' | 'manual';
  save(input: AutosaveSaveInput<Value>): Promise<AutosaveSaveResult<Value>>;
  readConflict?(error: unknown): AutosaveConflict<Value> | null;
  cloneValue?(value: Value): Value;
};

export function createAutosaveController<Value>(options: AutosaveOptions<Value>) {
  const cloneValue = options.cloneValue ?? ((value: Value) => globalThis.structuredClone(value));
  let value = cloneValue(options.initialValue);
  let savedValue = cloneValue(options.initialValue);
  let revision = options.initialRevision;
  let generation = 0;
  let dirty = false;
  let status: AutosaveStatus = 'Saved';
  let error: unknown | null = null;
  let conflict: AutosaveConflict<Value> | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inFlight: Promise<void> | null = null;
  let disposed = false;
  const listeners = new Set<() => void>();
  let snapshot = currentSnapshot();

  function currentSnapshot(): AutosaveSnapshot<Value> {
    return {
      status,
      value: cloneValue(value),
      revision,
      dirty,
      error,
      conflict: conflict
        ? {
            serverValue: cloneValue(conflict.serverValue),
            serverRevision: conflict.serverRevision,
          }
        : null,
    };
  }

  function publish() {
    snapshot = currentSnapshot();
    if (!disposed) {
      for (const listener of listeners) listener();
    }
  }

  function clearTimer() {
    if (timer !== undefined) {
      clearTimeout(timer);
      timer = undefined;
    }
  }

  function schedule() {
    clearTimer();
    if (options.mode === 'manual' || disposed || inFlight || conflict || !dirty) return;
    timer = setTimeout(() => {
      timer = undefined;
      void startSave();
    }, options.debounceMs);
  }

  function startSave(): Promise<void> {
    if (disposed || conflict || !dirty) return inFlight ?? Promise.resolve();
    if (inFlight) return inFlight;
    clearTimer();
    const submittedValue = cloneValue(value);
    const submittedGeneration = generation;
    const expectedRevision = revision;
    dirty = false;
    status = 'Saving';
    error = null;
    publish();

    const run = Promise.resolve()
      .then(() =>
        options.save({
          value: submittedValue,
          expectedRevision,
        }),
      )
      .then(
        (result) => {
          if (result.revision <= expectedRevision || result.revision <= revision) {
            dirty = true;
            status = 'Error';
            error = new Error('Autosave did not advance the saved revision');
            publish();
            return;
          }
          revision = result.revision;
          savedValue = cloneValue(result.value);
          if (generation === submittedGeneration && !dirty) {
            value = cloneValue(result.value);
            status = 'Saved';
          } else {
            dirty = true;
            status = options.mode === 'manual' ? 'Idle' : 'Saving';
          }
          publish();
        },
        (caught) => {
          const detected = options.readConflict?.(caught) ?? null;
          if (
            detected &&
            detected.serverRevision > expectedRevision &&
            detected.serverRevision > revision
          ) {
            conflict = {
              serverValue: cloneValue(detected.serverValue),
              serverRevision: detected.serverRevision,
            };
            status = 'Conflict';
            error = null;
          } else {
            status = 'Error';
            error = caught;
          }
          dirty = true;
          publish();
        },
      )
      .finally(() => {
        inFlight = null;
        if (options.mode !== 'manual' && !disposed && dirty && !conflict && status !== 'Error') {
          void startSave();
        }
      });
    inFlight = run;
    return run;
  }

  async function flush() {
    clearTimer();
    if (options.mode === 'manual') {
      if (status !== 'Error') await startSave();
      return;
    }
    while (!disposed && !conflict) {
      if (inFlight) {
        await inFlight;
        continue;
      }
      if (!dirty || status === 'Error') return;
      await startSave();
    }
  }

  return {
    getSnapshot() {
      return snapshot;
    },
    subscribe(listener: () => void) {
      if (disposed) return () => {};
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    edit(nextValue: Value) {
      if (disposed) return;
      value = cloneValue(nextValue);
      generation += 1;
      dirty = true;
      error = null;
      if (!conflict && !inFlight) status = 'Idle';
      publish();
      schedule();
    },
    discardChanges() {
      if (inFlight || conflict || disposed) return false;
      clearTimer();
      value = cloneValue(savedValue);
      generation += 1;
      dirty = false;
      error = null;
      status = 'Saved';
      publish();
      return true;
    },
    flush,
    async retry() {
      if (disposed || conflict) return;
      if (status === 'Error') {
        status = 'Idle';
        error = null;
        publish();
      }
      await flush();
    },
    copyLocal() {
      return cloneValue(value);
    },
    acceptServerVersion() {
      if (disposed || !conflict) return;
      clearTimer();
      value = cloneValue(conflict.serverValue);
      savedValue = cloneValue(conflict.serverValue);
      revision = conflict.serverRevision;
      generation += 1;
      dirty = false;
      status = 'Saved';
      error = null;
      conflict = null;
      publish();
    },
    reapplyLocal() {
      if (disposed || !conflict) return;
      revision = conflict.serverRevision;
      savedValue = cloneValue(conflict.serverValue);
      generation += 1;
      dirty = true;
      status = 'Idle';
      error = null;
      conflict = null;
      publish();
      schedule();
    },
    handleBeforeUnload(event: BeforeUnloadLike) {
      if (dirty || inFlight || conflict || status === 'Error') {
        event.preventDefault();
        event.returnValue = '';
      }
    },
    dispose() {
      disposed = true;
      clearTimer();
      listeners.clear();
    },
  };
}
